import React from "react";
import { Text, HStack, Tooltip, Icon } from "@astryxdesign/core";
import { Info } from "lucide-react";
import styles from "./CardLabel.module.css";

export interface CardLabelProps {
  label: string;
  /**
   * Definition of the card/metric, shown in a tooltip behind an info icon
   * after the label. Requires an Astryx <LayerProvider> ancestor.
   */
  info?: string;
  /** Content rendered before the label (e.g. a StatCard's tone StatusDot). */
  leading?: React.ReactNode;
  /** Content rendered after the label/info (e.g. a "Coming soon" Badge). */
  badge?: React.ReactNode;
}

/**
 * Shared header row for card-style tiles: an uppercase, letter-spaced
 * supporting label, with optional leading (e.g. a status dot), info tooltip,
 * and trailing badge slots. Extracted so every card-style tile (StatCard,
 * plain info cards, etc.) gets the same label treatment instead of
 * reimplementing it ad hoc.
 *
 * Renders via Astryx's Text/Tooltip/Icon, so this MUST be rendered inside an
 * Astryx <Theme> scope — and, because Tooltip renders into a layer, inside
 * an Astryx <LayerProvider>.
 */
const CardLabel = ({
  label,
  info,
  leading,
  badge,
}: CardLabelProps): React.ReactElement => {
  return (
    <HStack gap={2} align="center">
      {leading}
      <Text type="supporting" color="secondary" className={styles.label}>
        {label}
      </Text>
      {info && (
        <Tooltip content={info}>
          <Icon icon={Info} size="sm" color="secondary" />
        </Tooltip>
      )}
      {badge}
    </HStack>
  );
};

export default CardLabel;
